import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Users, Disc3, Music, Film, Tv, Loader2, Download, ExternalLink, ChevronRight } from 'lucide-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { search, importer } from '../api/client'
import type { SearchResult } from '../types'
import type { DabSearchResult } from '../api/client'

interface SearchBarProps {
  className?: string
}

const contentTypeIcons: Record<string, React.ElementType> = {
  artist: Users,
  album: Disc3,
  track: Music,
  movie: Film,
  episode: Tv,
  series: Tv,
}

const contentTypeLabels: Record<string, string> = {
  artist: 'Artists',
  album: 'Albums',
  track: 'Songs',
  movie: 'Movies',
  series: 'TV Shows',
  episode: 'Episodes',
}

// Define the order sections should appear
const sectionOrder = ['artist', 'track', 'album', 'movie', 'series', 'episode']

const contentTypeRoutes: Record<string, (id: string, parentId?: string) => string | null> = {
  artist: (id) => `/artists/${id}`,
  album: (id) => `/albums/${id}`,
  track: (_id, parentId) => parentId ? `/albums/${parentId}` : null, // Tracks link to their album via parent_id
  movie: (id) => `/movies/${id}`,
  series: (id) => `/tv/${id}`,
  episode: (_id, parentId) => parentId ? `/tv/${parentId}` : null, // Episodes link to their series via parent_id
}

export default function SearchBar({ className = '' }: SearchBarProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [dabResults, setDabResults] = useState<DabSearchResult[]>([])
  const [dabSearching, setDabSearching] = useState(false)
  const [dabDownloading, setDabDownloading] = useState<Set<string>>(new Set())
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Detect dab: prefix
  const isDabSearch = debouncedQuery.toLowerCase().startsWith('dab:')
  const dabQuery = isDabSearch ? debouncedQuery.slice(4).trim() : ''

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        inputRef.current?.blur()
      }
      // Ctrl/Cmd + K to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Only run local search if not a dab: search
  const { data: results, isLoading } = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: () => search(debouncedQuery, 50),
    enabled: debouncedQuery.length >= 2 && !isDabSearch,
  })

  // DAB search effect - when dab: prefix is detected
  useEffect(() => {
    if (!isDabSearch || dabQuery.length < 2) {
      setDabResults([])
      return
    }

    setDabSearching(true)
    importer.searchDab(dabQuery)
      .then(results => {
        setDabResults(results)
      })
      .catch(err => {
        console.error('DAB search failed:', err)
        setDabResults([])
      })
      .finally(() => {
        setDabSearching(false)
      })
  }, [isDabSearch, dabQuery])

  // DAB download mutation
  const downloadDabMutation = useMutation({
    mutationFn: async (result: DabSearchResult) => {
      return importer.downloadDabStandalone(result.album_id, result.artist, result.title)
    },
    onMutate: (result) => {
      setDabDownloading(prev => new Set(prev).add(result.album_id))
    },
    onSettled: (_, __, result) => {
      setDabDownloading(prev => {
        const next = new Set(prev)
        next.delete(result.album_id)
        return next
      })
    },
  })

  // Search DAB when local search has no results
  const handleSearchDab = () => {
    const searchQuery = debouncedQuery.trim()
    if (searchQuery.length < 2) return

    setDabSearching(true)
    importer.searchDab(searchQuery)
      .then(results => {
        setDabResults(results)
      })
      .catch(err => {
        console.error('DAB search failed:', err)
        setDabResults([])
      })
      .finally(() => {
        setDabSearching(false)
      })
  }

  // Group results by content type
  const groupedResults = results?.reduce<Record<string, SearchResult[]>>((acc, result) => {
    const type = result.content_type
    if (!acc[type]) acc[type] = []
    acc[type].push(result)
    return acc
  }, {})

  const handleResultClick = (result: SearchResult) => {
    const getRoute = contentTypeRoutes[result.content_type]
    if (getRoute) {
      const route = getRoute(result.id, result.parent_id)
      if (route) {
        navigate(route)
      }
    }
    setIsOpen(false)
    setQuery('')
  }

  const handleClear = () => {
    setQuery('')
    setDebouncedQuery('')
    inputRef.current?.focus()
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search... ar: al: s: m: t: e: dab: (Ctrl+K)"
          className="w-full pl-10 pr-10 py-2 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-200"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-dark-800 border border-dark-700 rounded-lg shadow-xl max-h-[70vh] overflow-y-auto z-50">
          {/* Loading state */}
          {(isLoading || dabSearching) ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-primary-400" />
              {dabSearching && <span className="ml-2 text-dark-400">Searching DAB...</span>}
            </div>
          ) : isDabSearch ? (
            /* DAB Search Results */
            dabResults.length === 0 ? (
              <div className="py-8 text-center text-dark-400">
                No DAB results found for "{dabQuery}"
              </div>
            ) : (
              <div className="py-2">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-primary-400 uppercase tracking-wider">
                  <ExternalLink size={14} />
                  DAB Results ({dabResults.length})
                </div>
                {dabResults.slice(0, 10).map((result) => (
                  <div
                    key={result.album_id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-dark-700 transition-colors rounded-md mx-1"
                    style={{ width: 'calc(100% - 8px)' }}
                  >
                    <div className="w-10 h-10 bg-dark-600 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {result.cover_url ? (
                        <img
                          src={result.cover_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Disc3 size={20} className="text-dark-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-dark-100 truncate text-sm font-medium">{result.title}</p>
                      <p className="text-dark-400 truncate text-xs">{result.artist} {result.year && `(${result.year})`}</p>
                      <p className="text-dark-500 text-xs">{result.quality} · {result.track_count} tracks</p>
                    </div>
                    <button
                      onClick={() => downloadDabMutation.mutate(result)}
                      disabled={dabDownloading.has(result.album_id)}
                      className="p-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Download album"
                    >
                      {dabDownloading.has(result.album_id) ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )
          ) : !results || results.length === 0 ? (
            /* No local results - show DAB button */
            <div className="py-6 text-center">
              <p className="text-dark-400 mb-4">No results found for "{query}"</p>
              {dabResults.length > 0 ? (
                /* Show DAB results after clicking button */
                <div className="text-left">
                  <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-primary-400 uppercase tracking-wider">
                    <ExternalLink size={14} />
                    DAB Results ({dabResults.length})
                  </div>
                  {dabResults.slice(0, 5).map((result) => (
                    <div
                      key={result.album_id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-dark-700 transition-colors rounded-md mx-1"
                      style={{ width: 'calc(100% - 8px)' }}
                    >
                      <div className="w-10 h-10 bg-dark-600 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {result.cover_url ? (
                          <img
                            src={result.cover_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Disc3 size={20} className="text-dark-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-dark-100 truncate text-sm font-medium">{result.title}</p>
                        <p className="text-dark-400 truncate text-xs">{result.artist} {result.year && `(${result.year})`}</p>
                        <p className="text-dark-500 text-xs">{result.quality} · {result.track_count} tracks</p>
                      </div>
                      <button
                        onClick={() => downloadDabMutation.mutate(result)}
                        disabled={dabDownloading.has(result.album_id)}
                        className="p-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Download album"
                      >
                        {dabDownloading.has(result.album_id) ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Download size={16} />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <button
                  onClick={handleSearchDab}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
                >
                  <ExternalLink size={16} />
                  Search DAB for "{query}"
                </button>
              )}
            </div>
          ) : (
            /* Normal local results */
            <div className="py-2 divide-y divide-dark-700">
              {sectionOrder.map((type) => {
                const items = groupedResults?.[type]
                if (!items || items.length === 0) return null

                const Icon = contentTypeIcons[type] || Music
                const label = contentTypeLabels[type] || type
                const displayItems = items.slice(0, 5) // Show max 5 per category
                const hasMore = items.length > 5
                return (
                  <div key={type} className="py-2 first:pt-0 last:pb-0">
                    <div className="flex items-center justify-between px-3 py-1.5">
                      <div className="flex items-center gap-2 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                        <Icon size={14} />
                        {label}
                      </div>
                      {hasMore && (
                        <button
                          onClick={() => {
                            navigate(`/search?q=${encodeURIComponent(debouncedQuery)}&type=${type}`)
                            setIsOpen(false)
                            setQuery('')
                          }}
                          className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300 transition-colors"
                        >
                          See all {items.length}
                          <ChevronRight size={14} />
                        </button>
                      )}
                    </div>
                    {displayItems.map((result) => (
                      <button
                        key={`${result.content_type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-dark-700 text-left transition-colors rounded-md mx-1"
                        style={{ width: 'calc(100% - 8px)' }}
                      >
                        <div className="w-8 h-8 bg-dark-600 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {result.image_path ? (
                            <img
                              src={`/api/image/${encodeURIComponent(result.image_path)}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Icon size={16} className="text-dark-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-dark-100 truncate text-sm font-medium">{result.title}</p>
                          {result.subtitle && (
                            <p className="text-dark-400 truncate text-xs">{result.subtitle}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )
              })}
              {/* See all results button */}
              {results && results.length > 0 && (
                <div className="pt-2 px-3 pb-1">
                  <button
                    onClick={() => {
                      navigate(`/search?q=${encodeURIComponent(debouncedQuery)}`)
                      setIsOpen(false)
                      setQuery('')
                    }}
                    className="w-full py-2 text-sm text-primary-400 hover:text-primary-300 hover:bg-dark-700 rounded-lg transition-colors flex items-center justify-center gap-1"
                  >
                    See all {results.length} results
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
